import { X, Ruler, User, Calendar, FileText } from 'lucide-react'
import { LoadingSpinner } from '../common'

function MeasurementPreviewModal({ 
  isOpen, 
  measurement, 
  customer,
  onClose,
  isLoading = false
}) {
  if (!isOpen) return null

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatMeasurement = (value) => {
    return value ? `${value}"` : 'Not specified'
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                  <Ruler className="h-6 w-6 text-primary-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-xl leading-6 font-bold text-secondary-900">
                    Measurement Preview
                  </h3>
                  <p className="text-base text-secondary-500">
                    {customer?.firstName} {customer?.lastName} - {measurement?.measurementType || 'Standard'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <LoadingSpinner />
              </div>
            ) : measurement ? (
              <div className="space-y-6">
                {/* Measurement Details */}
                <div className="bg-secondary-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-base font-medium text-secondary-700 mb-1">Measurement Type</h4>
                      <p className="text-lg font-semibold text-secondary-900">{measurement.measurementType || 'Standard'}</p>
                    </div>
                    <div>
                      <h4 className="text-base font-medium text-secondary-700 mb-1">Status</h4>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        measurement.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {measurement.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div>
                      <h4 className="text-base font-medium text-secondary-700 mb-1">Created</h4>
                      <div className="flex items-center text-base text-secondary-600">
                        <Calendar className="h-4 w-4 mr-2" />
                        {formatDate(measurement.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Upper Body Measurements */}
                <div>
                    <h4 className="text-lg font-bold text-secondary-900 mb-3 border-b border-secondary-200 pb-2">Upper Body Measurements</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {measurement.length && (
                      <div>
                        <p className="text-sm text-secondary-500 font-medium">Length</p>
                        <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.length)}</p>
                      </div>
                    )}
                    {measurement.upperchest && (
                      <div>
                        <p className="text-sm text-secondary-500 font-medium">Upper Chest</p>
                        <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.upperchest)}</p>
                      </div>
                    )}
                    {measurement.chest && (
                      <div>
                        <p className="text-sm text-secondary-500 font-medium">Chest</p>
                        <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.chest)}</p>
                      </div>
                    )}
                    {measurement.dartPoint && (
                      <div>
                        <p className="text-sm text-secondary-500 font-medium">Dart Point</p>
                        <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.dartPoint)}</p>
                      </div>
                    )}
                    {measurement.waist && (
                      <div>
                        <p className="text-sm text-secondary-500 font-medium">Waist</p>
                        <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.waist)}</p>
                      </div>
                    )}
                    {measurement.shoulder && (
                      <div>
                        <p className="text-sm text-secondary-500 font-medium">Shoulder</p>
                        <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.shoulder)}</p>
                      </div>
                    )}
                    {measurement.armHole && (
                      <div>
                        <p className="text-sm text-secondary-500 font-medium">Arm Hole</p>
                        <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.armHole)}</p>
                      </div>
                    )}
                    {measurement.biceps && (
                      <div>
                        <p className="text-sm text-secondary-500 font-medium">Biceps</p>
                        <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.biceps)}</p>
                      </div>
                    )}
                    {measurement.hip && (
                      <div>
                        <p className="text-sm text-secondary-500 font-medium">Hip</p>
                        <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.hip)}</p>
                      </div>
                    )}
                    {measurement.neckFront && (
                      <div>
                        <p className="text-sm text-secondary-500 font-medium">Neck Front</p>
                        <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.neckFront)}</p>
                      </div>
                    )}
                    {measurement.neckBack && (
                      <div>
                        <p className="text-sm text-secondary-500 font-medium">Neck Back</p>
                        <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.neckBack)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sleeve Measurements */}
                {(measurement.sleeveFull || measurement.sleeveHalf || measurement.sleeveShort || measurement.sleeveRound) && (
                  <div>
                    <h4 className="text-lg font-bold text-secondary-900 mb-3 border-b border-secondary-200 pb-2">Sleeve Measurements</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {measurement.sleeveFull && (
                        <div>
                          <p className="text-sm text-secondary-500 font-medium">Sleeve Full</p>
                          <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.sleeveFull)}</p>
                        </div>
                      )}
                      {measurement.sleeveHalf && (
                        <div>
                          <p className="text-sm text-secondary-500 font-medium">Sleeve Half</p>
                          <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.sleeveHalf)}</p>
                        </div>
                      )}
                      {measurement.sleeveShort && (
                        <div>
                          <p className="text-sm text-secondary-500 font-medium">Sleeve Short</p>
                          <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.sleeveShort)}</p>
                        </div>
                      )}
                      {measurement.sleeveRound && (
                        <div>
                          <p className="text-sm text-secondary-500 font-medium">Sleeve Round</p>
                          <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.sleeveRound)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Lower Body Measurements */}
                {(measurement.pentLength || measurement.pentWaist || measurement.thighRound || measurement.kneeRound || measurement.ankleRound) && (
                  <div>
                    <h4 className="text-lg font-bold text-secondary-900 mb-3 border-b border-secondary-200 pb-2">Lower Body Measurements</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {measurement.pentLength && (
                        <div>
                          <p className="text-sm text-secondary-500 font-medium">Pent Length</p>
                          <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.pentLength)}</p>
                        </div>
                      )}
                      {measurement.pentWaist && (
                        <div>
                          <p className="text-sm text-secondary-500 font-medium">Pent Waist</p>
                          <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.pentWaist)}</p>
                        </div>
                      )}
                      {measurement.thighRound && (
                        <div>
                          <p className="text-sm text-secondary-500 font-medium">Thigh Round</p>
                          <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.thighRound)}</p>
                        </div>
                      )}
                      {measurement.kneeRound && (
                        <div>
                          <p className="text-sm text-secondary-500 font-medium">Knee Round</p>
                          <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.kneeRound)}</p>
                        </div>
                      )}
                      {measurement.ankleRound && (
                        <div>
                          <p className="text-sm text-secondary-500 font-medium">Ankle Round</p>
                          <p className="text-xl font-bold text-secondary-900">{formatMeasurement(measurement.ankleRound)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {measurement.notes && (
                  <div>
                    <h4 className="text-lg font-bold text-secondary-900 mb-2 flex items-center">
                      <FileText className="h-4 w-4 mr-2" />
                      Notes
                    </h4>
                    <p className="text-base text-secondary-600 bg-secondary-50 rounded-lg p-3">
                      {measurement.notes}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <Ruler className="mx-auto h-12 w-12 text-secondary-400" />
                <h3 className="mt-2 text-base font-medium text-secondary-900">No measurement found</h3>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default MeasurementPreviewModal

